import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Key, Copy, RefreshCw, CheckCircle2, Webhook, ShoppingBag, Trash2 } from "lucide-react";

export default function Shopify() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shopify/api-key");
      setInfo(data);
    } catch (err) {
      // 404 is expected on first visit (no key generated yet) — silently swallow.
      // Anything else, surface to console for debugging.
      if (err.response?.status !== 404) {
        console.error("[Shopify] Failed to load API key:", err);
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/shopify/api-key", { label: "My Shopify Store" });
      toast.success("API key generated — save it securely");
      await fetch();
    } catch (err) { toast.error("Failed to generate"); }
    finally { setGenerating(false); }
  };

  const revoke = async () => {
    if (!confirm("Revoke API key? This will break your Shopify webhook until you generate a new one.")) return;
    try {
      await api.post("/shopify/api-key/revoke");
      toast.success("Key revoked");
      await fetch();
    } catch (err) { toast.error("Failed"); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(""), 2000);
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="shopify-page">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 flex items-center gap-2"><ShoppingBag className="h-6 w-6 text-[#FF6B00]" /> Shopify Integration</h1>
        <p className="text-sm text-slate-500 mt-0.5">Auto-create PickNGo shipments when Shopify orders are placed.</p>
      </div>

      {!info?.active ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center" data-testid="no-key-state">
          <Key className="h-12 w-12 text-[#FF6B00] mx-auto mb-3" />
          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-1">Generate API Key</h2>
          <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">One click to create your Shopify webhook URL. You'll add this to your Shopify admin.</p>
          <Button onClick={generate} disabled={generating} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-6" data-testid="generate-key-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />} Generate API Key
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="key-info">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-['Outfit'] font-bold text-slate-900">Your API Key</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={generate} disabled={generating} className="rounded-lg" data-testid="rotate-key-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Rotate
                </Button>
                <Button variant="outline" size="sm" onClick={revoke} className="rounded-lg border-red-200 text-red-600" data-testid="revoke-key-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">API Key</p>
                <div className="flex gap-2">
                  <Input readOnly value={info.api_key} className="font-mono text-xs bg-slate-50" data-testid="api-key-input" />
                  <Button variant="outline" size="sm" onClick={() => copy(info.api_key, "API key")} className="rounded-lg" data-testid="copy-api-key">
                    {copied === "API key" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1"><Webhook className="h-3 w-3" /> Webhook URL</p>
                <div className="flex gap-2">
                  <Input readOnly value={info.webhook_url} className="font-mono text-xs bg-slate-50" data-testid="webhook-url-input" />
                  <Button variant="outline" size="sm" onClick={() => copy(info.webhook_url, "Webhook URL")} className="rounded-lg" data-testid="copy-webhook">
                    {copied === "Webhook URL" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100">
                <Info label="Orders Created" value={info.orders_created} />
                <Info label="Last Used" value={info.last_used_at ? new Date(info.last_used_at).toLocaleString() : "—"} />
                <Info label="Created" value={info.created_at ? new Date(info.created_at).toLocaleDateString() : "—"} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-['Outfit'] font-bold text-slate-900 mb-3">Setup in 3 Steps</h2>
            <ol className="space-y-3 text-sm text-slate-700">
              <Step n={1} title="Open Shopify admin">Go to <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">Settings → Notifications → Webhooks</span></Step>
              <Step n={2} title="Create a webhook">Event: <strong>Order creation</strong> · Format: <strong>JSON</strong> · URL: paste the webhook URL above</Step>
              <Step n={3} title="Done!">When an order is placed on Shopify, we'll auto-create a PickNGo shipment and return a tracking ID. Syncs in real time.</Step>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value ?? "—"}</p>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-3">
      <span className="h-6 w-6 rounded-full bg-[#FF6B00] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
      <div>
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{children}</p>
      </div>
    </li>
  );
}
